import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, useFormikContext, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { 
  ArrowLeft, 
  Save, 
  Trash2, 
  Edit3, 
  User, 
  Image as ImageIcon, 
  Brain, 
  ClipboardList, 
  X,
  AlertCircle,
  FileText,
  Clock,
  History,
  Activity,
  Search,
  Stethoscope,
  HeartPulse,
  ClipboardCheck,
  Calendar
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { patients } from '../services/api';
import AIChat from '../components/AIChat';
import MedicalImages from '../components/MedicalImages';
import ConfirmationDialog from '../components/ConfirmationDialog';
import AIBriefing from '../components/AIBriefing';
import PatientActivityLog from '../components/PatientActivityLog';
import { useTranslation } from 'react-i18next';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

interface Patient {
  id?: string;
  name: string;
  age: number;
  gender: string;
  hospital_id: string;
  visit_date: string;
  chief_complaint: string;
  current_illness: string;
  past_history: string;
  physical_examination: string;
  auxiliary_examination: string;
  diagnosis: string;
  treatment_suggestion: string;
  management_suggestion: string;
  images: string[];
  created_at: string;
  updated_at: string;
}

const FormErrorMessage = ({ name }: { name: string }) => (
  <ErrorMessage
    name={name}
    render={(msg) => <div className="text-destructive text-xs mt-1 font-medium px-1">{msg}</div>}
  />
);

const FormObserver = ({ onChange }: { onChange: (values: any, initialValues: any) => void }) => {
  const { values, initialValues } = useFormikContext();
  useEffect(() => {
    onChange(values, initialValues);
  }, [values, initialValues, onChange]);
  return null;
};

// Reusable display component for read-only mode
const DisplayField = ({ label, value, icon: Icon, className = "" }: { label: string, value?: string | number, icon?: any, className?: string }) => (
  <div className={`space-y-1.5 ${className}`}>
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground/80">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </div>
    <div className="text-base font-medium text-gray-900 leading-relaxed min-h-[1.5rem]">
      {value || <span className="text-muted-foreground/40 italic text-sm">Not recorded</span>}
    </div>
  </div>
);

const PatientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNavigationConfirm, setShowNavigationConfirm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { t } = useTranslation();

  const isNewPatient = id === 'new';

  const PatientSchema = useMemo(() => Yup.object().shape({
    name: Yup.string()
      .required(t('validation.nameRequired'))
      .max(50, t('validation.nameMaxLength')),
    age: Yup.number()
      .required(t('validation.ageRequired'))
      .positive(t('validation.agePositive'))
      .integer(t('validation.ageInteger'))
      .max(150, t('validation.ageMax')),
    gender: Yup.string()
      .required(t('validation.genderRequired'))
      .oneOf(['male', 'female'], t('validation.genderValues')),
    hospital_id: Yup.string().max(50, t('validation.hospitalIdMaxLength')),
    visit_date: Yup.string().max(50, t('validation.visitDateMaxLength')),
    chief_complaint: Yup.string().max(1000, t('validation.chiefComplaintMaxLength')),
    current_illness: Yup.string().max(5000, t('validation.currentIllnessMaxLength')),
    past_history: Yup.string().max(5000, t('validation.pastHistoryMaxLength')),
    physical_examination: Yup.string().max(5000, t('validation.physicalExamMaxLength')),
    auxiliary_examination: Yup.string().max(5000, t('validation.auxiliaryExamMaxLength')),
    diagnosis: Yup.string().max(1000, t('validation.diagnosisMaxLength')),
    treatment_suggestion: Yup.string().max(1000, t('validation.treatmentSuggestionMaxLength')),
    management_suggestion: Yup.string().max(1000, t('validation.managementSuggestionMaxLength')),
  }), [t]);

  const defaultInitialValues = useMemo<Patient>(() => ({
    name: '',
    age: 60,
    gender: '',
    hospital_id: '',
    visit_date: '',
    chief_complaint: '',
    current_illness: '',
    past_history: '',
    physical_examination: '',
    auxiliary_examination: '',
    diagnosis: '',
    treatment_suggestion: '',
    management_suggestion: '',
    images: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }), []);
  
  const [initialValues, setInitialValues] = useState<Patient>(defaultInitialValues);

  const fetchPatient = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await patients.getById(id!);
      if (!data) {
        setError(t('patientDetail.errors.failedToLoad'));
        setLoading(false);
        return;
      }
      if (data.visit_date && !data.visit_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        try {
          const date = new Date(data.visit_date);
          if (!isNaN(date.getTime())) {
            data.visit_date = date.toISOString().split('T')[0];
          }
        } catch (e) { console.warn(e); }
      }
      setPatient(data);
      setInitialValues(data);
    } catch (error) {
      setError(t('patientDetail.errors.loadingError'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    if (!isNewPatient) {
      fetchPatient();
      setIsEditMode(false);
    } else {
      setIsEditMode(true);
      setInitialValues(defaultInitialValues);
      setLoading(false);
    }
  }, [id, isNewPatient, fetchPatient, defaultInitialValues]);

  const handleFormChange = useCallback((values: Patient, initialValues: Patient) => {
    if (isEditMode) {
      const hasFormChanges = JSON.stringify(values) !== JSON.stringify(initialValues);
      setHasChanges(hasFormChanges);
      if (validationError) setValidationError(null);
    }
  }, [isEditMode, validationError]);

  const toggleEditMode = () => {
    if (isEditMode && hasChanges) {
      if (window.confirm(t('patientDetail.errors.unsavedChanges'))) {
        setIsEditMode(false);
        setHasChanges(false);
      }
    } else {
      setIsEditMode(!isEditMode);
    }
  };

  const handleNavigateBack = () => {
    if (hasChanges) {
      setShowNavigationConfirm(true);
    } else {
      navigate('/dashboard');
    }
  };

  const handleSave = async (values: Patient, { setSubmitting, validateForm }: any) => {
    try {
      setError(null);
      setValidationError(null);
      const errors = await validateForm(values);
      if (Object.keys(errors).length > 0) {
        setValidationError(t('patientDetail.errors.formValidation'));
        setSubmitting(false);
        return;
      }
      setLoading(true);
      const patientData = { ...values, images: values.images || [] };
      if (isNewPatient) {
        await patients.create(patientData);
        navigate('/dashboard');
      } else {
        await patients.update(id!, patientData);
        await fetchPatient();
        setHasChanges(false);
        setIsEditMode(false);
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || t('patientDetail.errors.saveFailed'));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await patients.delete(id!);
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.response?.data?.detail || t('patientDetail.errors.deleteError'));
      setTimeout(() => navigate('/dashboard'), 1500);
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-[60vh] gap-3">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="text-sm text-muted-foreground animate-pulse">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-screen relative left-[50%] right-[50%] -ml-[50vw] -mr-[50vw] -mt-4 md:-mt-6 bg-[#f8fafc] bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:32px_32px]">
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-4 md:pt-6 space-y-8 pb-20">
        {/* Error Display */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl flex items-center justify-between shadow-sm"
            >
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
                <span>{error}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setError(null)} className="h-8 hover:bg-red-100">
                <X className="h-4 w-4" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Enhanced Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={handleNavigateBack} className="rounded-2xl h-12 w-12 bg-gray-50 hover:bg-gray-100">
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
                {isNewPatient ? t('patientDetail.newPatient') : patient?.name}
              </h1>
              {!isNewPatient && (
                <Badge variant="secondary" className={`text-sm px-3 py-1 rounded-xl font-bold ${patient?.gender === 'male' ? 'bg-blue-50 text-blue-700' : 'bg-pink-50 text-pink-700'}`}>
                  {patient?.gender === 'male' ? t('patientDetail.genderOptions.male') : t('patientDetail.genderOptions.female')} • {patient?.age} {t('patientDetail.fields.age')}
                </Badge>
              )}
            </div>
            {!isNewPatient && (
              <div className="flex items-center gap-4 text-sm text-muted-foreground font-medium">
                <span className="flex items-center gap-1.5"><Search className="h-4 w-4" /> ID: {patient?.hospital_id || 'N/A'}</span>
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {t('patientList.createdAt')}: {new Date(patient?.created_at || '').toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isNewPatient && (
            <Button
              variant={isEditMode ? "ghost" : "outline"}
              onClick={toggleEditMode}
              className="rounded-2xl h-11 px-6 font-bold transition-all"
            >
              {isEditMode ? <X className="h-4 w-4 mr-2" /> : <Edit3 className="h-4 w-4 mr-2" />}
              {isEditMode ? t('common.cancelEdit') : t('common.edit')}
            </Button>
          )}
          {(isEditMode || isNewPatient) && (
            <Button
              onClick={() => document.querySelector('form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))}
              disabled={!isNewPatient && !hasChanges}
              className="rounded-2xl h-11 px-8 font-bold bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all"
            >
              <Save className="h-4 w-4 mr-2" />
              {t('common.save')}
            </Button>
          )}
          {!isNewPatient && isEditMode && (
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              className="rounded-2xl h-11 w-11 shadow-lg shadow-destructive/10"
            >
              <Trash2 className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex items-center w-full max-w-4xl rounded-2xl bg-gray-100/80 p-1.5 mb-8">
          <TabsTrigger value="info" className="flex-1 rounded-xl py-2.5 font-bold transition-all data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary">
            <User className="h-4 w-4 mr-2" />
            {t('patientDetail.personalInfo')}
          </TabsTrigger>
          <TabsTrigger value="images" className="flex-1 rounded-xl py-2.5 font-bold transition-all data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary" disabled={isNewPatient}>
            <ImageIcon className="h-4 w-4 mr-2" />
            {t('patientDetail.medicalImages')}
          </TabsTrigger>
          <TabsTrigger value="activities" className="flex-1 rounded-xl py-2.5 font-bold transition-all data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary" disabled={isNewPatient}>
            <Activity className="h-4 w-4 mr-2" />
            {t('patientDetail.activityLog')}
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex-1 rounded-xl py-2.5 font-bold transition-all data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary" disabled={isNewPatient}>
            <Brain className="h-4 w-4 mr-2" />
            {t('patientDetail.aiAssistant')}
          </TabsTrigger>
          <TabsTrigger value="briefing" className="flex-1 rounded-xl py-2.5 font-bold transition-all data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary" disabled={isNewPatient}>
            <ClipboardList className="h-4 w-4 mr-2" />
            {t('patientDetail.medicalBriefing')}
          </TabsTrigger>
        </TabsList>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <TabsContent value="info" className="mt-0 outline-none">
              <Formik
                initialValues={initialValues}
                validationSchema={PatientSchema}
                onSubmit={handleSave}
                enableReinitialize={true}
              >
                {({ errors, touched }) => (
                  <Form className="space-y-8">
                    <FormObserver onChange={handleFormChange} />
                    
                    {validationError && (
                      <motion.div 
                        initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                        className="bg-red-50 border-l-4 border-red-500 text-red-800 px-6 py-4 rounded-2xl flex items-center shadow-sm"
                      >
                        <AlertCircle className="h-6 w-6 mr-3 text-red-500" />
                        <span className="font-bold">{validationError}</span>
                      </motion.div>
                    )}

                    {/* Main Information Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                      {/* Left Column: Core Medical Data */}
                      <div className="lg:col-span-8 space-y-8">
                        {/* Diagnosis Card - Highlighted */}
                        <Card className="rounded-2xl border-none shadow-xl shadow-slate-900/5 overflow-hidden ring-1 ring-slate-200">
                          <CardHeader className="bg-gray-600 text-white px-6 py-3">
                            <CardTitle className="text-xl flex items-center gap-3">
                              <Stethoscope className="h-6 w-6 text-slate-100" />
                              {t('patientDetail.fields.diagnosis')}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-8 bg-slate-100/50">
                            {isEditMode ? (
                              <>
                                <Field name="diagnosis">
                                  {({ field }: any) => (
                                    <Textarea {...field} className="min-h-[120px] rounded-2xl text-lg font-medium p-4 border-blue-200 focus:border-blue-500 focus:ring-blue-100 bg-white" placeholder={t('patientDetail.fields.diagnosis')} />
                                  )}
                                </Field>
                                <FormErrorMessage name="diagnosis" />
                              </>
                            ) : (
                              <div className="text-2xl font-bold text-slate-900 leading-relaxed whitespace-pre-wrap">
                                {patient?.diagnosis || <span className="text-muted-foreground/30 italic font-medium">{t('common.noData')}</span>}
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        {/* Clinical History Sections */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <Card className="rounded-2xl border-none shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col h-full ring-1 ring-slate-200">
                            <CardHeader className="bg-gray-600 text-white px-6 py-3">
                              <CardTitle className="text-lg flex items-center gap-2.5">
                                <Activity className="h-5 w-5 text-slate-100" />
                                {t('patientDetail.fields.chiefComplaint')}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="p-6 flex-grow bg-slate-100/50">
                              {isEditMode ? (
                                <Field name="chief_complaint">
                                  {({ field }: any) => <Textarea {...field} className="min-h-[100px] rounded-xl border-purple-100 bg-white" />}
                                </Field>
                              ) : (
                                <div className="text-slate-800 leading-relaxed whitespace-pre-wrap font-medium">
                                  {patient?.chief_complaint || t('common.noData')}
                                </div>
                              )}
                              {isEditMode && <FormErrorMessage name="chief_complaint" />}
                            </CardContent>
                          </Card>

                          <Card className="rounded-2xl border-none shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col h-full ring-1 ring-slate-200">
                            <CardHeader className="bg-gray-600 text-white px-6 py-3">
                              <CardTitle className="text-lg flex items-center gap-2.5">
                                <History className="h-5 w-5 text-slate-100" />
                                {t('patientDetail.fields.currentIllness')}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="p-6 flex-grow bg-slate-100/50">
                              {isEditMode ? (
                                <Field name="current_illness">
                                  {({ field }: any) => <Textarea {...field} className="min-h-[150px] rounded-xl border-orange-100 bg-white" />}
                                </Field>
                              ) : (
                                <div className="text-slate-800 leading-relaxed whitespace-pre-wrap font-medium">
                                  {patient?.current_illness || t('common.noData')}
                                </div>
                              )}
                              {isEditMode && <FormErrorMessage name="current_illness" />}
                            </CardContent>
                          </Card>
                        </div>

                        {/* Examination Sections */}
                        <Card className="rounded-2xl border-none shadow-sm hover:shadow-md transition-all overflow-hidden ring-1 ring-slate-200">
                          <CardHeader className="bg-gray-600 text-white px-6 py-3">
                            <CardTitle className="text-lg flex items-center gap-2.5">
                              <ClipboardCheck className="h-5 w-5 text-slate-100" />
                              {t('patientDetail.fields.physicalExamination')}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6 bg-slate-100/50">
                            {isEditMode ? (
                              <Field name="physical_examination">
                                {({ field }: any) => <Textarea {...field} className="min-h-[100px] rounded-xl border-emerald-100 bg-white" />}
                              </Field>
                            ) : (
                              <div className="text-slate-800 leading-relaxed whitespace-pre-wrap font-medium">
                                {patient?.physical_examination || t('common.noData')}
                              </div>
                            )}
                            {isEditMode && <FormErrorMessage name="physical_examination" />}
                          </CardContent>
                        </Card>

                        <Card className="rounded-2xl border-none shadow-sm hover:shadow-md transition-all overflow-hidden ring-1 ring-slate-200">
                          <CardHeader className="bg-gray-600 text-white px-6 py-3">
                            <CardTitle className="text-lg flex items-center gap-2.5">
                              <HeartPulse className="h-5 w-5 text-slate-100" />
                              {t('patientDetail.fields.auxiliaryExamination')}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6 bg-slate-100/50">
                            {isEditMode ? (
                              <Field name="auxiliary_examination">
                                {({ field }: any) => <Textarea {...field} className="min-h-[100px] rounded-xl border-rose-100 bg-white" />}
                              </Field>
                            ) : (
                              <div className="text-slate-800 leading-relaxed whitespace-pre-wrap font-medium">
                                {patient?.auxiliary_examination || t('common.noData')}
                              </div>
                            )}
                            {isEditMode && <FormErrorMessage name="auxiliary_examination" />}
                          </CardContent>
                        </Card>
                      </div>

                      {/* Right Column: Sidebar Panels */}
                      <div className="lg:col-span-4 space-y-8">
                        {/* Basic Info Sidebar Card */}
                        <Card className="rounded-2xl border-none shadow-xl shadow-gray-200/50 overflow-hidden ring-1 ring-slate-200">
                          <CardHeader className="bg-gray-600 text-white px-6 py-3">
                            <CardTitle className="text-lg flex items-center gap-2 font-bold">
                              <FileText className="h-5 w-5 text-slate-100" />
                              {t('patientDetail.basicInfo')}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6 space-y-6 bg-slate-100/50">
                            {isEditMode ? (
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <label className="text-xs font-bold text-slate-500 uppercase">{t('patientDetail.fields.name')} *</label>
                                  <Field name="name" as={Input} className="rounded-xl border-slate-200 bg-white" />
                                  <FormErrorMessage name="name" />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-xs font-bold text-slate-500 uppercase">{t('patientDetail.fields.hospitalId')}</label>
                                  <Field name="hospital_id" as={Input} className="rounded-xl border-slate-200 bg-white" />
                                  <FormErrorMessage name="hospital_id" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase">{t('patientDetail.fields.age')} *</label>
                                    <Field name="age" type="number" as={Input} className="rounded-xl border-slate-200 bg-white" />
                                    <FormErrorMessage name="age" />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase">{t('patientDetail.fields.gender')} *</label>
                                    <div className="flex gap-3 h-10 items-center">
                                      <label className="flex items-center gap-1.5 cursor-pointer">
                                        <Field type="radio" name="gender" value="male" className="h-4 w-4 text-primary" />
                                        <span className="text-sm font-medium text-slate-700">{t('patientDetail.genderOptions.male')}</span>
                                      </label>
                                      <label className="flex items-center gap-1.5 cursor-pointer">
                                        <Field type="radio" name="gender" value="female" className="h-4 w-4 text-primary" />
                                        <span className="text-sm font-medium text-slate-700">{t('patientDetail.genderOptions.female')}</span>
                                      </label>
                                    </div>
                                    <FormErrorMessage name="gender" />
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <label className="text-xs font-bold text-slate-500 uppercase">{t('patientDetail.fields.visitDate')}</label>
                                  <Field name="visit_date" type="date" as={Input} className="rounded-xl border-slate-200 bg-white" />
                                  <FormErrorMessage name="visit_date" />
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-6">
                                <DisplayField label={t('patientDetail.fields.name')} value={patient?.name} icon={User} />
                                <DisplayField label={t('patientDetail.fields.hospitalId')} value={patient?.hospital_id} icon={Search} />
                                <div className="grid grid-cols-2 gap-4">
                                  <DisplayField label={t('patientDetail.fields.age')} value={patient?.age} />
                                  <DisplayField label={t('patientDetail.fields.gender')} value={patient?.gender === 'male' ? t('patientDetail.genderOptions.male') : t('patientDetail.genderOptions.female')} />
                                </div>
                                <DisplayField label={t('patientDetail.fields.visitDate')} value={patient?.visit_date} icon={Calendar} />
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        {/* Suggestions Sidebar Panel */}
                        <Card className="rounded-2xl border-none shadow-sm overflow-hidden ring-1 ring-slate-200">
                          <CardHeader className="bg-gray-600 text-white px-6 py-3">
                            <CardTitle className="text-base flex items-center gap-2 font-bold">
                              <HeartPulse className="h-5 w-5 text-slate-100" />
                              {t('patientDetail.fields.treatmentSuggestion')}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6 bg-slate-100/50">
                            {isEditMode ? (
                              <Field name="treatment_suggestion">
                                {({ field }: any) => <Textarea {...field} className="min-h-[100px] rounded-xl border-indigo-200 bg-white" />}
                              </Field>
                            ) : (
                              <div className="text-slate-800 text-sm leading-relaxed font-medium whitespace-pre-wrap">
                                {patient?.treatment_suggestion || t('common.noData')}
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        <Card className="rounded-2xl border-none shadow-sm overflow-hidden ring-1 ring-slate-200">
                          <CardHeader className="bg-gray-600 text-white px-6 py-3">
                            <CardTitle className="text-base flex items-center gap-2 font-bold">
                              <ClipboardCheck className="h-5 w-5 text-slate-100" />
                              {t('patientDetail.fields.managementSuggestion')}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6 bg-slate-100/50">
                            {isEditMode ? (
                              <Field name="management_suggestion">
                                {({ field }: any) => <Textarea {...field} className="min-h-[100px] rounded-xl border-teal-200 bg-white" />}
                              </Field>
                            ) : (
                              <div className="text-slate-800 text-sm leading-relaxed font-medium whitespace-pre-wrap">
                                {patient?.management_suggestion || t('common.noData')}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </TabsContent>

            <TabsContent value="images" className="mt-0 outline-none">
              <Card className="rounded-2xl border-none shadow-xl shadow-gray-200/50 overflow-hidden ring-1 ring-slate-200">
                <CardHeader className="bg-gray-600 text-white px-6 py-3">
                  <CardTitle className="text-lg flex items-center gap-2 font-bold">
                    <ImageIcon className="h-5 w-5 text-slate-100" />
                    {t('patientDetail.medicalImages')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8 bg-slate-100/50">
                  <MedicalImages patientId={id!} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activities" className="mt-0 outline-none">
              <Card className="rounded-2xl border-none shadow-xl shadow-gray-200/50 overflow-hidden ring-1 ring-slate-200">
                <CardHeader className="bg-gray-600 text-white px-6 py-3">
                  <CardTitle className="text-lg flex items-center gap-2 font-bold">
                    <Activity className="h-5 w-5 text-slate-100" />
                    {t('patientDetail.activityLog')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8 bg-slate-100/50">
                  <PatientActivityLog patientId={id!} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="ai" className="mt-0 outline-none">
               <Card className="rounded-2xl border-none shadow-2xl shadow-slate-900/10 overflow-hidden min-h-[700px] ring-1 ring-slate-200">
                <CardHeader className="bg-gray-600 text-white px-6 py-3">
                  <CardTitle className="text-lg flex items-center gap-2 font-bold">
                    <Brain className="h-5 w-5 text-slate-100" />
                    {t('patientDetail.aiAssistant')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 h-full bg-white">
                  {patient && <AIChat patientId={id!} patient={patient} />}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="briefing" className="mt-0 outline-none">
              <Card className="rounded-2xl border-none shadow-xl shadow-slate-900/5 overflow-hidden ring-1 ring-slate-200">
                <CardHeader className="bg-gray-600 text-white px-6 py-3">
                  <CardTitle className="text-lg flex items-center gap-2 font-bold">
                    <ClipboardList className="h-5 w-5 text-slate-100" />
                    {t('patientDetail.medicalBriefing')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8 bg-slate-100/50">
                  <AIBriefing patientId={id!} patientName={patient?.name} />
                </CardContent>
              </Card>
            </TabsContent>
          </motion.div>
        </AnimatePresence>
      </Tabs>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        title={t('patientDetail.deletePatient')}
        message={t('patientDetail.deleteConfirmation')}
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
        iconName="Warning"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <ConfirmationDialog
        isOpen={showNavigationConfirm}
        title={t('patientDetail.errors.unsavedChanges')}
        message={t('patientDetail.errors.navigationConfirmation')}
        confirmText={t('common.leave')}
        cancelText={t('common.cancel')}
        onConfirm={() => { setShowNavigationConfirm(false); navigate('/dashboard'); }}
        onCancel={() => setShowNavigationConfirm(false)}
      />
    </div>
  </div>
);
};

export default PatientDetail;
